import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface CustomerSummary {
  customer_name: string
  revenue_ytd: number
  active_orders: number
  backlog: number
  margin_pct: number
  hours_total: number
  receivables_open: number
  overdue: number
  dso_days: number | null
  active_contracts: number
  monthly_recurring: number
  concentration_pct: number
}

export interface CustomerOrder {
  order_id: string
  order_value: number
  budget_cost: number
  order_date: string | null
  service_line: string | null
  status: string
  cost_to_date: number
  hours_worked: number
  margin_pct: number
}

export interface CustomerInvoice {
  invoice_id: string
  invoice_date: string | null
  due_date: string | null
  outstanding: number
  status: string
  overdue: boolean
}

export interface CustomerContract {
  contract_id: string
  service_line: string | null
  recurring_value: number
  recurring_period: number
  monthly_value: number
  start_date: string | null
  end_date: string | null
  auto_renewal: boolean
}

export interface PaymentHistoryEntry {
  invoice_date: string | null
  paid_date: string | null
  amount: number
  days_to_pay: number | null
}

export interface CustomerKpis {
  customer_name: string
  revenue_ytd: number
  revenue_by_month: { month: number; amount: number }[]
  active_orders_count: number
  avg_margin_pct: number
  total_hours: number
  total_receivables: number
  total_overdue: number
  avg_payment_days: number | null
  active_contracts_count: number
  monthly_recurring: number
  orders: CustomerOrder[]
  invoices_open: CustomerInvoice[]
  contracts: CustomerContract[]
  payment_history: PaymentHistoryEntry[]
  computed_at: string
}

export function useCustomers(refDate?: string) {
  return useQuery<CustomerSummary[]>({
    queryKey: ['customers', refDate],
    queryFn: () => {
      const params = refDate ? `?ref_date=${refDate}` : ''
      return api.get<CustomerSummary[]>(`/api/v1/customers${params}`)
    },
  })
}

export function useCustomerKpis(customerName: string, refDate?: string) {
  return useQuery<CustomerKpis>({
    queryKey: ['customer-kpis', customerName, refDate],
    queryFn: () => {
      const params = refDate ? `?ref_date=${refDate}` : ''
      return api.get<CustomerKpis>(`/api/v1/customers/${encodeURIComponent(customerName)}/kpis${params}`)
    },
    enabled: !!customerName,
  })
}
